/**
 * Offer Service - API Abstraction Layer
 * Centralizes all offer-related API calls
 * Used by useOfferEdit hook and any component needing offer data
 */

import axios from 'axios';
import { API_BASE } from '../config/api';

/**
 * Offer Service
 * Provides methods for creating, updating, and fetching offers
 */
export const offerService = {
  /**
   * Create a new special offer
   * @param {Object} offerData - Offer data
   * @param {string} offerData.park_id - Park ID
   * @param {string} offerData.category - Park category
   * @param {string} offerData.offer_title - Offer title
   * @param {string} offerData.offer_description - Offer description
   * @param {string} offerData.offer_terms - Offer terms
   * @param {string} offerData.business_email - Business email (optional)
   * @param {string} offerData.expiration_date - ISO date string
   * @param {number} offerData.subscription_months - Subscription duration
   * @param {string} token - JWT token
   * @returns {Promise} - Axios response
   */
  createOffer: async (offerData, token) => {
    return axios.post(
      `${API_BASE}/admin/special-offers`,
      offerData,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  },

  /**
   * Update an existing special offer
   * @param {string} offerId - Offer ID
   * @param {Object} updateData - Data to update
   * @param {string} token - JWT token
   * @returns {Promise} - Axios response
   */
  updateOffer: async (offerId, updateData, token) => {
    return axios.put(
      `${API_BASE}/admin/special-offers/${offerId}`,
      updateData,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  },

  /**
   * Get subscription status for a park
   * @param {string} parkId - Park ID
   * @returns {Promise} - Axios response with subscription data
   */
  getSubscription: async (parkId) => {
    return axios.get(`${API_BASE}/business/subscription-status/${parkId}`);
  },

  /**
   * Get all special offers
   * @returns {Promise} - Axios response with offers array
   */
  getAllOffers: async () => {
    return axios.get(`${API_BASE}/special-offers`);
  },

  /**
   * Get offers for a specific park
   * @param {string} parkId - Park ID
   * @param {string} token - JWT token (optional, for admin access)
   * @returns {Promise} - Axios response with offers array
   */
  getOffersByPark: async (parkId, token = null) => {
    const config = token 
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    
    return axios.get(`${API_BASE}/special-offers?park_id=${parkId}`, config);
  },

  /**
   * Delete a special offer
   * @param {string} offerId - Offer ID
   * @param {string} token - JWT token
   * @returns {Promise} - Axios response
   */
  deleteOffer: async (offerId, token) => {
    return axios.delete(
      `${API_BASE}/admin/special-offers/${offerId}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  }
};

export default offerService;
